import { useState } from 'react';
import { useKaydedilmemisIcerik } from '../shell/KaydedilmemisIcerik';
import { TASK_HEDEF_OLAMAZ_SAPKALAR, gorevTanimiKapanisMetni } from '@isg/validation';
import type { GorevTanimiKapanisIslemi } from '@isg/validation';
import {
  Alert,
  Button,
  Group,
  Modal,
  MultiSelect,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import type { RoleType } from '@isg/shared-types';
import { ROLE_TYPES } from '@isg/shared-types';
import {
  useGorevHedefDurumu,
  useGorevSecenekleri,
  useGorevTanimiAc,
  useGorevTanimiGuncelle,
  useGorevTanimiSil,
  useGorevTanimlari,
} from '../api/queries';
import { SAPKA_ETIKET } from '../i18n/tr';

/** T15 — askı süzgeci. "Askıda" = şablonu gizlenmiş tanım (türetilen hâl). */
const ASKI_SECENEKLERI = [
  { value: 'hepsi', label: 'Tümü' },
  { value: 'calisan', label: 'Çalışan' },
  { value: 'askida', label: 'Askıda' },
];
import { QueryStatus } from './parts';
import { Rozet, SatirEylemleri, SolukSatir } from '../sozlesme';
import { HedefDurumuSatirlari } from './gorev-parts';
import { ANLAM } from '../theme';

/**
 * PERİYODİK GÖREV TANIMLARI (F4·S5·B2 — T10/#4; sayfa sözleşmesi P1 · P1-a ·
 * P1-b · P2 · P2-a).
 *
 * ⚠️ **GÖREV TANIMI BİR ALT KALEMDİR, BAĞIMSIZ KAYIT DEĞİL** (P1'in YAPISAL
 * ayrımı: *kendi sayfası olabilir mi?* — olamaz; tek başına anlamı yoktur,
 * nesnesiyle birlikte okunur). Bu yüzden **satır içi** girilir ve ekranda
 * *"Ekle"* düğmesi **yoktur**: listenin sonundaki **soluk satır** zaten
 * oradadır ve kaydedince **altında yenisi belirir**.
 *
 * ⚠️ **BOŞ LİSTEDE DE YALNIZ SOLUK SATIR DURUR** — *"henüz kayıt yok"* kutusu
 * YAZILMAZ (P1-a): kullanıcıya ne yapacağını anlatmak yerine yapabileceği
 * yeri gösteririz.
 *
 * ⚠️ **SİLME ⋮ ALTINDADIR** (P2-a): geri alınamayan eylem satırda kırmızı ve
 * tek tık uzakta durmaz.
 *
 * ⚠️ **TEKNİSYEN BU EKRANDA YAZAMAZ** (T10/#4 — *teknisyen görev TANIMLAMAZ,
 * yalnız UYGULAR*). Karar **SUNUCUDAN** gelir (`options.tanimlayabilir`);
 * ekran ikinci bir şapka listesi tutmaz — görünmeyen soluk satırın ucu da
 * 403 verir çünkü ikisi aynı listeden konuşur.
 */
export function GorevTanimlariScreen(): React.JSX.Element {
  // T15 (F4·S6) — ASKI SÜZGECİ: askıdaki tanım listede GÖRÜNÜR (sessizce
  // düşmez) ve tek başına süzülebilir. Süzgeç SUNUCUDA koşar.
  const [askiSuzgeci, setAskiSuzgeci] = useState<'hepsi' | 'askida' | 'calisan'>('hepsi');
  const tanimlar = useGorevTanimlari(
    askiSuzgeci === 'hepsi' ? undefined : askiSuzgeci === 'askida',
  );
  const secenekler = useGorevSecenekleri();
  const ac = useGorevTanimiAc();
  const guncelle = useGorevTanimiGuncelle();
  const sil = useGorevTanimiSil();
  const [taslak, setTaslak] = useState<Taslak>(BOS_TASLAK);
  // MADDE 293 — *Kapat* ve *Tanımı sil* ÖNCE SORAR: açık görevler geçersiz
  // sayılacak ve yeniden açmak o dönemi geri getirmeyecek. Sayı satırın
  // kendisinden (sunucu — `acikGorevSayisi`); ekran saymaz. *Aç* sormaz.
  const [onay, setOnay] = useState<{
    id: string;
    islem: GorevTanimiKapanisIslemi;
    acikGorevSayisi: number;
  } | null>(null);
  const onayMetni = onay ? gorevTanimiKapanisMetni(onay.islem, onay.acikGorevSayisi) : null;
  const onayla = (): void => {
    if (!onay) return;
    const kapat = { onSuccess: () => setOnay(null), onError: () => setOnay(null) };
    if (onay.islem === 'sil') sil.mutate(onay.id, kapat);
    else guncelle.mutate({ id: onay.id, input: { aktif: false } }, kapat);
  };

  const tanimlayabilir = secenekler.data?.tanimlayabilir ?? false;
  const dokunuldu = taslak !== BOS_TASLAK;
  // P12 (madde 224) — kaydedilmemiş görev tanımı çıkışta uyarır (yönlendirici tek noktada).
  useKaydedilmemisIcerik(dokunuldu ? 1 : 0, 'görev tanımı');
  // C1 — kodlanmış nesne değerinden iki alan çözülür (XOR ekranda da tektir).
  const isyeriSecildi = taslak.nesne?.startsWith('isyeri:') ?? false;
  const ekipmanId = taslak.nesne?.startsWith('ekipman:')
    ? taslak.nesne.slice('ekipman:'.length)
    : null;
  const isyeriId = isyeriSecildi ? taslak.nesne!.slice('isyeri:'.length) : null;
  const kaydedilebilir =
    taslak.nesne !== null &&
    // ⚠️ İŞYERİ NESNESİNDE YÜKÜMLÜLÜK KALEMİ ZORUNLU — kapı SUNUCUDA (şema
    // 422 verir); buradaki kontrol yalnız düğmeyi pasif tutar, yerine geçmez.
    (!isyeriSecildi || taslak.yukumlulukKalemRef !== null) &&
    taslak.formTemplateId !== null &&
    (taslak.hedefTipi === 'kisi'
      ? taslak.hedefPersonId !== null
      : taslak.kumeKaynagi === 'operator_listesi' || taslak.kumeSapkalar.length > 0);

  // GÖREV ATAMA UYARISI (Kapsam §22.6a): nesne + hedef seçilince sunucunun
  // yüklemi sorulur — liste satırının taşıdığı yüklemin AYNISI.
  const hedefOnizleme = useGorevHedefDurumu(
    kaydedilebilir
      ? {
          ekipmanId,
          isyeriId,
          hedefTipi: taslak.hedefTipi,
          hedefPersonId: taslak.hedefTipi === 'kisi' ? taslak.hedefPersonId : null,
          kumeKaynagi: taslak.hedefTipi === 'kume' ? taslak.kumeKaynagi : null,
          kumeSapkalar: taslak.hedefTipi === 'kume' ? taslak.kumeSapkalar : [],
        }
      : null,
  );

  const kaydet = (): void => {
    if (!kaydedilebilir) return;
    ac.mutate(
      {
        ...(ekipmanId
          ? { ekipmanId }
          : { isyeriId, yukumlulukKalemRef: taslak.yukumlulukKalemRef }),
        formTemplateId: taslak.formTemplateId ?? undefined,
        periyotBicimi: taslak.periyotBicimi,
        hedefTipi: taslak.hedefTipi,
        ...(taslak.hedefTipi === 'kisi'
          ? { hedefPersonId: taslak.hedefPersonId }
          : { kumeKaynagi: taslak.kumeKaynagi, kumeSapkalar: taslak.kumeSapkalar }),
      } as Parameters<typeof ac.mutate>[0],
      // Kaydedince taslak sıfırlanır → SOLUK SATIR ALTINDA YENİSİ BELİRİR.
      { onSuccess: () => setTaslak(BOS_TASLAK) },
    );
  };

  return (
    <Stack>
      <Title order={4}>Görev kuralları</Title>
      {/* ⚠️ **E3 — SEKME KENDİNİ ANLATIR (§P6).** Ayrım bir ÜRETİM ilişkisidir
          ve söylenmeden görünmez. ✏️ D2-4 (17.09.2026 — bulgu 81): paragraflar
          kalktı, TEK SATIR kaldı (aynası *Periyodik görevler*de). */}
      <Text size="xs" c="dimmed" data-testid="sekme-iliski-cumlesi">
        Her satır bir <strong>kuraldır</strong>; vadesi geldikçe Periyodik görevler&apos;de iş
        doğurur.
      </Text>
      <Group gap="xs" align="flex-end" data-testid="gorev-tanim-suzgec">
        <Stack gap={2}>
          <Text size="xs" c="dimmed">
            Askı
          </Text>
          <SegmentedControl
            size="xs"
            data={ASKI_SECENEKLERI}
            value={askiSuzgeci}
            onChange={(value) => setAskiSuzgeci(value as 'hepsi' | 'askida' | 'calisan')}
            data-testid="gorev-tanim-aski-suzgeci"
          />
        </Stack>
      </Group>
      <QueryStatus queries={[tanimlar, secenekler]} />
      {(ac.isError || guncelle.isError || sil.isError) && (
        <Alert color={ANLAM.engel} role="alert">
          {(ac.error ?? guncelle.error ?? sil.error)?.message}
        </Alert>
      )}

      {tanimlar.data && (
        <Table striped data-testid="gorev-tanim-listesi">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Nesne</Table.Th>
              <Table.Th>Kalem (form)</Table.Th>
              <Table.Th>Periyot</Table.Th>
              <Table.Th>Hedef</Table.Th>
              <Table.Th>Durum</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {tanimlar.data.map((tanim) => (
              <Table.Tr key={tanim.id} data-testid="gorev-tanim-satiri">
                <Table.Td>
                  {tanim.nesne.ad}
                  <Text size="xs" c="dimmed">
                    {tanim.nesne.workplaceAd}
                  </Text>
                  {/* C1 — işyeri nesnesinde HANGİ yükümlülük olduğu satırda
                      görünür; yoksa satır *"işyerinin görevi"* der ve neyin
                      görevi olduğu okunamaz. */}
                  {tanim.yukumlulukKalemAd !== null && (
                    <Text size="xs" c="dimmed">
                      {tanim.yukumlulukKalemAd}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>{tanim.kalemAd}</Table.Td>
                <Table.Td>
                  {/* FAIL-LOUD'UN EKRAN YÜZÜ: periyodu çözülemeyen kalem için
                      görev ÜRETİLMEZ ve bu SÖYLENİR — uydurulmuş bir
                      varsayılan, periyodu olmayan bir görevden tehlikelidir. */}
                  {tanim.periyotAy === null ? (
                    <Rozet anlam="engel" testId="periyot-tanimsiz">
                      Periyot tanımsız
                    </Rozet>
                  ) : (
                    `${tanim.periyotAy} ay · ${tanim.periyotBicimi === 'kayan' ? 'kayan' : 'takvim'}`
                  )}
                </Table.Td>
                <Table.Td>
                  {tanim.hedefTipi === 'kisi'
                    ? (tanim.hedefAd ?? '—')
                    : tanim.kumeKaynagi === 'operator_listesi'
                      ? 'Operatör listesi'
                      : tanim.kumeSapkalar.map((rol) => SAPKA_ETIKET[rol] ?? rol).join(', ')}
                  {/* GÖREV ATAMA UYARISI — kalıcı işaret; TÜRETİLİR (kişiye hesap
                      verilince kendiliğinden kalkar). */}
                  <HedefDurumuSatirlari
                    durum={tanim.hedefDurumu}
                    kume={tanim.hedefTipi === 'kume'}
                  />
                </Table.Td>
                <Table.Td>
                  {/* G6 — HEDEFSİZ ATAMA SESSİZ KALMAZ: bildirim de üretir,
                      ekran da söyler. Küme boşaldıysa görev kimseye düşmez. */}
                  {/* ⚠️ T15 (F4·S6) — ASKI: sebebi ŞABLONUN GİZLENMESİDİR ve
                      SAKLANMAZ, TÜRETİLİR. Şablon geri gösterilince rozet
                      kendiliğinden düşer ve üretim devam eder — kullanıcının
                      ayrıca bir şey yapması gerekmez, metin bunu SÖYLER.
                      ⚠️ `aktif`in ÖNÜNDE okunur: askıdaki tanım "Aktif"
                      görünseydi ekran YALAN söylerdi (görev üretilmiyor). */}
                  {tanim.askida ? (
                    <Rozet anlam="dikkat" testId="askida-rozeti">
                      Askıda — şablonu gizlendi
                    </Rozet>
                  ) : tanim.hedefsiz ? (
                    <Rozet anlam="dikkat" testId="hedefsiz-rozeti">
                      Hedefsiz
                    </Rozet>
                  ) : tanim.aktif ? (
                    <Rozet anlam="olumlu">Aktif</Rozet>
                  ) : (
                    <Rozet anlam="bilgi">Kapalı</Rozet>
                  )}
                </Table.Td>
                <Table.Td>
                  <SatirEylemleri
                    testId="gorev-tanim-eylemleri"
                    birincil={
                      tanimlayabilir
                        ? {
                            etiket: tanim.aktif ? 'Kapat' : 'Aç',
                            onClick: () =>
                              tanim.aktif
                                ? setOnay({
                                    id: tanim.id,
                                    islem: 'kapat',
                                    acikGorevSayisi: tanim.acikGorevSayisi,
                                  })
                                : guncelle.mutate({ id: tanim.id, input: { aktif: true } }),
                            testId: 'gorev-tanim-acik-kapali',
                          }
                        : undefined
                    }
                    digerleri={[
                      {
                        etiket: 'Tanımı sil',
                        tehlikeli: true,
                        gizli: !tanimlayabilir,
                        onClick: () =>
                          setOnay({
                            id: tanim.id,
                            islem: 'sil',
                            acikGorevSayisi: tanim.acikGorevSayisi,
                          }),
                        testId: 'gorev-tanim-sil',
                      },
                    ]}
                  />
                </Table.Td>
              </Table.Tr>
            ))}

            {/* P1-a — SOLUK SATIR: listenin SONUNDA, her zaman. Yetkisi
                olmayan kullanıcıda hiç çizilmez (karar sunucudan). */}
            {tanimlayabilir && (
              <SolukSatir
                dokunuldu={dokunuldu}
                kaydedilebilir={kaydedilebilir}
                kaydediliyor={ac.isPending}
                onKaydet={kaydet}
                kolonSayisi={5}
                testId="gorev-tanim-soluk-satir"
              >
                <Table.Td>
                  {/* ✏️ **TUR 8 / C1 — İŞYERİ NESNESİ AÇILDI.** Sunucu iki
                      nesne türünü F4·S4'ten beri kabul ediyordu; ekran yalnız
                      ekipman sunuyordu ve yükümlülük görevlerinin **yolu hiç
                      yoktu** (§2.40: *"uç var, yol yok"*). Gruplar türü
                      söyler; kodlanmış değer XOR'u ekranda da tek tutar. */}
                  <Stack gap={4}>
                    <Select
                      size="xs"
                      placeholder="Ekipman ya da işyeri"
                      searchable
                      comboboxProps={{ withinPortal: true }}
                      data={[
                        {
                          group: 'Ekipman',
                          items: (secenekler.data?.ekipmanlar ?? []).map((e) => ({
                            value: `ekipman:${e.id}`,
                            label: `${e.ad} — ${e.workplaceAd}`,
                          })),
                        },
                        {
                          group: 'İşyeri (yasal yükümlülük)',
                          items: (secenekler.data?.isyerleri ?? []).map((w) => ({
                            value: `isyeri:${w.id}`,
                            label: w.ad,
                          })),
                        },
                      ]}
                      value={taslak.nesne}
                      onChange={(deger) =>
                        setTaslak((onceki) => ({
                          ...onceki,
                          nesne: deger,
                          // Tür değişince kalem seçimi DÜŞER: ekipman
                          // nesnesinde kalem taşımak 422'dir.
                          yukumlulukKalemRef: deger?.startsWith('isyeri:')
                            ? onceki.yukumlulukKalemRef
                            : null,
                        }))
                      }
                      data-testid="soluk-ekipman"
                    />
                    {isyeriSecildi && (
                      <Select
                        size="xs"
                        placeholder="Yükümlülük kalemi"
                        searchable
                        comboboxProps={{ withinPortal: true }}
                        data={(secenekler.data?.yukumlulukKalemleri ?? []).map((k) => ({
                          value: k.id,
                          label: k.ad,
                        }))}
                        value={taslak.yukumlulukKalemRef}
                        onChange={(deger) =>
                          setTaslak((onceki) => ({ ...onceki, yukumlulukKalemRef: deger }))
                        }
                        data-testid="soluk-yukumluluk-kalemi"
                      />
                    )}
                  </Stack>
                </Table.Td>
                <Table.Td>
                  <Select
                    size="xs"
                    placeholder="Form şablonu"
                    searchable
                    data={(secenekler.data?.formSablonlari ?? []).map((f) => ({
                      value: f.id,
                      label: f.ad,
                    }))}
                    value={taslak.formTemplateId}
                    onChange={(deger) =>
                      setTaslak((onceki) => ({ ...onceki, formTemplateId: deger }))
                    }
                    data-testid="soluk-sablon"
                  />
                </Table.Td>
                <Table.Td>
                  <Select
                    size="xs"
                    data={[
                      { value: 'kayan', label: 'Kayan' },
                      { value: 'takvim', label: 'Takvim' },
                    ]}
                    value={taslak.periyotBicimi}
                    onChange={(deger) =>
                      setTaslak((onceki) => ({
                        ...onceki,
                        periyotBicimi: (deger ?? 'kayan') as Taslak['periyotBicimi'],
                      }))
                    }
                    data-testid="soluk-periyot-bicimi"
                  />
                </Table.Td>
                <Table.Td>
                  {/* ✅ **B5 (Tur 4½) — *"Küme — operatör listesi"* SEÇENEĞİ
                        GERİ AÇILDI ve K4'ün gizleme gerekçesi DÜŞTÜ.**

                        Tur 4'te seçenek kaldırılmıştı çünkü
                        `equipment_operator` listesinin bir **yazma yüzeyi
                        hiç yoktu**: seçenek her zaman BOŞ kümeye çözülüyor,
                        atama hedefsiz doğuyor ve her gün bildirim
                        üretiyordu (§2.40 — *yalan söyleyen kontrol*).
                        Kaldırma bilinçliydi ve şerhi *"listeyi yöneten
                        yüzeyle BİRLİKTE geri gelir"* diyordu; o yüzey bu
                        turda açıldı.

                        ⚠️ **KÜMENİN ANLAMI DEĞİŞTİ (§25/7):** artık *"bu
                        ekipmana elle eklenmiş kişiler"* değil, **ekipmanın
                        TİPİNE göre o tipin operatör GÖREVLENDİRMESİ olan
                        kişilerdir**; `equipment_operator` SÖKÜLDÜ. Yazma
                        yüzeyi kişi kartındaki görevlendirme bölümüdür.

                        ⚠️ **HEDEFSİZ ATAMA UYARISI (G6) AYNEN SÜRER** —
                        küme yine boşalabilir (o tipte görevlendirilmiş
                        kimse yoksa), ama bu artık kullanıcının
                        **kapatabileceği** bir durumdur: görevlendirmeyi
                        verir, küme dolar. */}
                  <Select
                    size="xs"
                    placeholder="Hedef"
                    data={[
                      { value: 'kisi', label: 'Kişi' },
                      { value: 'kume-sapka', label: 'Küme — şapka + bahçe' },
                      { value: 'kume-operator', label: 'Küme — ekipman tipinin operatörleri' },
                    ]}
                    value={hedefDegeri(taslak)}
                    onChange={(deger) => setTaslak((onceki) => hedefSec(onceki, deger))}
                    data-testid="soluk-hedef-tipi"
                  />
                </Table.Td>
                <Table.Td>
                  {/* P1-b — alan çoksa satır GENİŞLER: hedef ayrıntısı burada,
                      ayrı sayfaya KAÇILMAZ. */}
                  {taslak.hedefTipi === 'kisi' ? (
                    <Select
                      size="xs"
                      placeholder="Kişi"
                      searchable
                      data={(secenekler.data?.personeller ?? []).map((p) => ({
                        value: p.id,
                        label: p.adSoyad,
                      }))}
                      value={taslak.hedefPersonId}
                      onChange={(deger) =>
                        setTaslak((onceki) => ({ ...onceki, hedefPersonId: deger }))
                      }
                      data-testid="soluk-hedef-kisi"
                    />
                  ) : taslak.kumeKaynagi === 'sapka_bahce' ? (
                    <MultiSelect
                      size="xs"
                      placeholder="Şapkalar"
                      // Satırdaki nesne ve kalem seçicileriyle AYNI hizada:
                      // tablo hücresi açılır listeyi kırpabilir (tur 4½ /
                      // B1'in *"görünüyor · çalışıyor · KULLANILAMIYOR"*
                      // bulgusu). ⚠️ Bu bir HİZALAMADIR, bir düzeltme değil —
                      // portalsız hâlin bir arıza ürettiği ÖLÇÜLMEDİ.
                      comboboxProps={{ withinPortal: true }}
                      data={SECILEBILIR_SAPKALAR.map((rol) => ({
                        value: rol,
                        label: SAPKA_ETIKET[rol] ?? rol,
                      }))}
                      value={taslak.kumeSapkalar}
                      onChange={(deger) =>
                        setTaslak((onceki) => ({ ...onceki, kumeSapkalar: deger as RoleType[] }))
                      }
                      data-testid="soluk-hedef-sapkalar"
                    />
                  ) : (
                    <Text size="xs" c="dimmed" data-testid="soluk-hedef-operator-notu">
                      Bu ekipmanın tipinde operatör görevlendirmesi olan kişiler
                    </Text>
                  )}
                  {/* ATAMA ANINDA UYARI: sistem uyarır AMA ATAR — kaydet açık kalır. */}
                  {hedefOnizleme.data && (
                    <div data-testid="soluk-hedef-durumu">
                      <HedefDurumuSatirlari
                        durum={hedefOnizleme.data}
                        kume={taslak.hedefTipi === 'kume'}
                      />
                    </div>
                  )}
                </Table.Td>
              </SolukSatir>
            )}
          </Table.Tbody>
        </Table>
      )}

      {/* MADDE 293 — onay penceresi (P1: modal yalnız ONAY sorusunda). Metin
          `@isg/validation`dan; sayı sunucudan. */}
      <Modal
        opened={onay !== null}
        onClose={() => setOnay(null)}
        title={onayMetni?.baslik}
        data-testid="gorev-tanim-onay"
      >
        <Stack>
          {onayMetni?.satirlar.map((satir) => (
            <Text key={satir} size="sm" data-testid="gorev-tanim-onay-metni">
              {satir}
            </Text>
          ))}
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => setOnay(null)}
              data-testid="gorev-tanim-onay-vazgec"
            >
              Vazgeç
            </Button>
            <Button
              color={onay?.islem === 'sil' ? ANLAM.engel : undefined}
              loading={guncelle.isPending || sil.isPending}
              onClick={onayla}
              data-testid="gorev-tanim-onay-onayla"
            >
              {onayMetni?.onay}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

interface Taslak {
  /**
   * ✏️ **TUR 8 / C1 — NESNE ARTIK İKİ TÜRLÜ.** Değer `ekipman:<id>` ya da
   * `isyeri:<id>` biçiminde KODLANIR; iki ayrı alan tutmak, ikisinin birden
   * dolu olabildiği bir hâl açardı ve XOR'u ekranda ikinci kez doğrulamak
   * gerekirdi (şema zaten *"tam olarak bir nesne"* der).
   */
  nesne: string | null;
  /** İşyeri nesnesinde ZORUNLU — periyot bu kalemden çözülür. */
  yukumlulukKalemRef: string | null;
  formTemplateId: string | null;
  periyotBicimi: 'kayan' | 'takvim';
  hedefTipi: 'kisi' | 'kume';
  hedefPersonId: string | null;
  kumeKaynagi: 'operator_listesi' | 'sapka_bahce' | null;
  kumeSapkalar: RoleType[];
}

const BOS_TASLAK: Taslak = {
  nesne: null,
  yukumlulukKalemRef: null,
  formTemplateId: null,
  periyotBicimi: 'kayan',
  hedefTipi: 'kisi',
  hedefPersonId: null,
  kumeKaynagi: null,
  kumeSapkalar: [],
};

/** İzleyici ve yüklenici temsilcisi göreve HEDEF OLAMAZ — liste kuralın KENDİ
 *  sabitinden türetilir (`TASK_HEDEF_OLAMAZ_SAPKALAR`; P13-b — ekran süzgeç yazmaz). */
const SECILEBILIR_SAPKALAR: readonly RoleType[] = ROLE_TYPES.filter(
  (rol) => !TASK_HEDEF_OLAMAZ_SAPKALAR.includes(rol),
);

function hedefDegeri(taslak: Taslak): string {
  if (taslak.hedefTipi === 'kisi') return 'kisi';
  return taslak.kumeKaynagi === 'sapka_bahce' ? 'kume-sapka' : 'kume-operator';
}

function hedefSec(taslak: Taslak, deger: string | null): Taslak {
  if (deger === 'kisi') {
    return { ...taslak, hedefTipi: 'kisi', kumeKaynagi: null, kumeSapkalar: [] };
  }
  if (deger === 'kume-sapka') {
    return { ...taslak, hedefTipi: 'kume', kumeKaynagi: 'sapka_bahce', hedefPersonId: null };
  }
  return {
    ...taslak,
    hedefTipi: 'kume',
    kumeKaynagi: 'operator_listesi',
    hedefPersonId: null,
    kumeSapkalar: [],
  };
}
